import { Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { usePartyDateFormat } from 'customHooks/usePartyDateTimeFormat'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles({ name: 'DateRangeFilter' })((theme) => ({
  dateFilter: {
    display: 'flex',
    gap: theme.spacing(2),
    '& .MuiTextField-root': {
      width: '200px',
    },
    '& input': {
      fontSize: theme.typography.body1.fontSize,
    },
    '& svg': {
      fontSize: theme.typography.body1.fontSize,
    },
  },
}))

export interface DateRangeFilterProps {
  /** The selected start date of the range. Can be null if no date is selected. */
  fromDate: Date | null

  /** The selected end date of the range. Can be null if no date is selected. */
  toDate: Date | null

  /** Callback function called when the from date changes. Receives the new date or null if cleared. */
  onFromDateChange: (date: Date | null) => void

  /** Callback function called when the to date changes. Receives the new date or null if cleared. */
  onToDateChange: (date: Date | null) => void

  /**
   * The minimum allowed date for both date pickers.
   * @default moment().subtract(5, 'years')
   */
  minDate?: moment.Moment

  /**
   * The maximum allowed date for both date pickers.
   * If not provided, the current date is used as the maximum.
   */
  maxDate?: moment.Moment

  /**
   * Placeholder text for the from date input field.
   * @default t('common:created-between', 'created between')
   */
  fromDatePlaceholder?: string

  /**
   * Placeholder text for the to date input field.
   * @default t('common:created-on', 'created on')
   */
  toDatePlaceholder?: string

  /**
   * Label text displayed above the from date picker.
   * If not provided, no label is shown.
   */
  fromDateLabel?: string

  /**
   * Label text displayed above the to date picker.
   * If not provided, no label is shown.
   */
  toDateLabel?: string

  /**
   * The visual variant of the input fields.
   * @default 'standard'
   */
  variant?: 'standard' | 'outlined' | 'filled'

  /**
   * The size of the input fields.
   * @default 'small'
   */
  size?: 'small' | 'medium'

  /**
   * Whether to show the "and" text between the two date pickers.
   * Useful for inline layouts where the relationship is clear.
   * @default true
   */
  showAndText?: boolean

  /**
   * Custom date format string. If not provided, uses the party's default date format.
   * Should follow moment.js format patterns (e.g., 'DD/MM/YYYY', 'MM-DD-YYYY').
   */
  dateFormat?: string

  /**
   * Additional CSS class name to apply to the root container.
   * Can be used to override styling or add custom styles.
   */
  className?: string
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  minDate = moment().subtract(5, 'years'),
  maxDate,
  fromDatePlaceholder,
  toDatePlaceholder,
  fromDateLabel,
  toDateLabel,
  variant = 'standard',
  size = 'small',
  showAndText = true,
  dateFormat,
  className,
}) => {
  const { classes } = useStyles()
  const { t } = useTranslation()
  const partyDefaultDateFormat = usePartyDateFormat()

  const [fromDateError, setFromDateError] = useState<string | null>(null)
  const [toDateError, setToDateError] = useState<string | null>(null)

  const effectiveDateFormat = dateFormat ?? partyDefaultDateFormat

  return (
    <div className={`${classes.dateFilter} ${className || ''}`}>
      <DatePicker
        value={fromDate ? moment(fromDate) : null}
        onChange={(date) => {
          onFromDateChange(date ? date.toDate() : null)
        }}
        onError={(error) => {
          setFromDateError(error ? error : null)
        }}
        format={effectiveDateFormat}
        disableFuture
        minDate={minDate}
        maxDate={toDate ? moment(toDate) : maxDate}
        label={fromDateLabel}
        slotProps={{
          field: {
            clearable: true,
            onClear: () => {
              onFromDateChange(null)
            },
          },
          textField: {
            size: size,
            variant: variant as 'standard',
            slotProps: {
              inputLabel: {
                shrink: true,
              },
              htmlInput: {
                placeholder:
                  fromDatePlaceholder ||
                  t('common:created-between', 'created between'),
              },
            },
            error: Boolean(fromDateError || toDateError),
            helperText: fromDateError
              ? t(`errors:${fromDateError}`, fromDateError)
              : null,
          },
        }}
      />
      {showAndText && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            alignSelf: 'center',
          }}
        >
          {t('common:and', 'and')}
        </Typography>
      )}
      <DatePicker
        onError={(error) => {
          setToDateError(error ? error : null)
        }}
        value={toDate ? moment(toDate) : moment(new Date())}
        onChange={(date) => {
          onToDateChange(date?.toDate() ?? new Date())
        }}
        minDate={fromDate ? moment(fromDate) : undefined}
        maxDate={maxDate}
        format={effectiveDateFormat}
        disableFuture
        formatDensity="dense"
        label={toDateLabel}
        slotProps={{
          field: {
            clearable: true,
            onClear: () => {
              onToDateChange(new Date())
            },
          },
          textField: {
            size: size,
            variant: variant as 'standard',
            slotProps: {
              inputLabel: {
                shrink: true,
              },
              htmlInput: {
                placeholder:
                  toDatePlaceholder || t('common:created-on', 'created on'),
              },
            },
            error: Boolean(fromDateError || toDateError),
            helperText: toDateError
              ? t(`errors:${toDateError}`, toDateError)
              : null,
          },
        }}
      />
    </div>
  )
}

export default DateRangeFilter
